import React, { useEffect, useState } from "react";
import "./App.scss";
import { ONBOARDING_STEP, OnboardingData } from "./constants/app.constants";
import { ImageCarousel } from "./components/image-carousel/image-carousel.component";
import { WelcomeStepComponent } from "./components/onboarding-steps/welcome-step/welcome-step.component";
import { getCookieFromStorage } from "./utils/utils";
import { Client, DroplrUser, JwtAuth } from "droplr-client-ts";
import { StepProgressComponent } from "./components/step-progress/step-progress.component";
import { WhoIsUsingStepComponent } from "./components/onboarding-steps/who-is-using-step/who-is-using-step.component";
import { TeamIntroComponent } from "./components/onboarding-steps/team-intro/team-intro-step.component";
import { DroplrThemeProvider } from "droplr-react-ui-v2";
import { UsageStepComponent } from "./components/onboarding-steps/usage-step/usage-step.component";
import { AppDownloadsComponent } from "./components/app-downloads/app-downloads.component";
import { FinishStepComponent } from "./components/onboarding-steps/finish-step/finish-step.component";

const App = () => {
  const [CurrentStep, SetCurrentStep] = useState(ONBOARDING_STEP.WELCOME);
  const [UserFirstName, SetUserFirstName] = useState("");
  const [DataLoaded, SetDataLoaded] = useState(false);
  const [SwappingStepsAnimation, SetSwappingStepsAnimation] = useState(false);
  const [IsOnLastStep, SetIsOnLastStep] = useState(false);
  const [OnboardingData, SetOnboardingData] = useState<OnboardingData>({
    completed: false,
    items: {},
  });

  /**
   * Buffers the step state update a bit
   * to allow for the animations to play out
   */
  const PostponeStepChange = (NewStep: ONBOARDING_STEP) => {
    if (NewStep === ONBOARDING_STEP.FINISH) {
      SetIsOnLastStep(true);
    } else {
      SetSwappingStepsAnimation(true);
    }
    setTimeout(() => {
      SetCurrentStep(NewStep);
      SetSwappingStepsAnimation(false);
    }, 1000);
  };

  useEffect(() => {
    const URLParams = new URLSearchParams(window.location.search);
    let JwtToken = URLParams.get("jwtToken");
    /**
     * Fetch the user's data from the API and check if the token is properly set within the cookies
     */
    if (JwtToken == null) {
      console.log("No token found.");
      window.location.href = process.env.REACT_APP_AUTH_URL!;
      return;
    }

    const DroplrClient = new Client({
      baseUrl: process.env.REACT_APP_API_URL!,
      auth: new JwtAuth(JwtToken),
    });
    DroplrClient.Users.Current()
      .then((response) => {
        if (!response) return;
        response.json().then((User: DroplrUser) => {
          if (typeof (User as any).firstName === "undefined")
            SetUserFirstName("");
          else SetUserFirstName((User as any).firstName);
          SetDataLoaded(true);
        });
      })
      .catch((err: any) => {
        SetUserFirstName("");
        console.log("There was an error fetching the user's data: ", err);
        window.location.href = process.env.REACT_APP_AUTH_URL!;
      });
  }, []);

  /**
   * Swaps the component based on the step
   */
  const GetStepComponent = () => {
    switch (CurrentStep) {
      case ONBOARDING_STEP.WELCOME:
        return (
          <WelcomeStepComponent
            SetCurrentStep={PostponeStepChange}
            UserFirstName={UserFirstName}
            DataLoaded={DataLoaded}
          />
        );
      case ONBOARDING_STEP.WHO_IS_USING:
        return (
          <WhoIsUsingStepComponent
            SetCurrentStep={PostponeStepChange}
            OnComplete={(option: any) => {
              SetOnboardingData({
                ...OnboardingData,
                items: {
                  ...OnboardingData!.items,
                  whoIsUsing: option,
                },
              });
            }}
          />
        );
      case ONBOARDING_STEP.TEAM_INTRO:
        return (
          <TeamIntroComponent
            SetCurrentStep={PostponeStepChange}
            OnComplete={(teamName: any, teamSize: any) => {
              SetOnboardingData({
                ...OnboardingData,
                items: {
                  ...OnboardingData!.items,
                  teamSize: teamSize,
                  teamName: teamName,
                },
              });
            }}
          />
        );
      case ONBOARDING_STEP.USAGE:
        return (
          <UsageStepComponent
            SetCurrentStep={PostponeStepChange}
            GetOnboardingData={() => OnboardingData}
            OnComplete={(option: any) => {
              SetOnboardingData({
                ...OnboardingData,
                completed: true,
                items: {
                  ...OnboardingData!.items,
                  usingDroplrFor: option,
                },
              });
            }}
          />
        );
      case ONBOARDING_STEP.FINISH:
        return <FinishStepComponent />;
    }
  };

  return (
    <div className="App">
      <DroplrThemeProvider theme={"light"}>
        <div className="app-container">
          <StepProgressComponent CurrentStep={CurrentStep} />
          {/* 
          The contents of the steps are within the step-container
           */}
          <div
            className={[
              "step-container",
              SwappingStepsAnimation && "fade-out",
            ].join(" ")}
          >
            {GetStepComponent()}
          </div>
          {CurrentStep === ONBOARDING_STEP.FINISH ? (
            <AppDownloadsComponent />
          ) : (
            <ImageCarousel
              CurrentStep={CurrentStep}
            />
          )}
        </div>
      </DroplrThemeProvider>
    </div>
  );
};

export default App;
